<template>
  <div class="bg-surface flex flex-col h-full">
    <!-- Header Bar -->
    <div class="flex items-center gap-3 px-4 py-3 shrink-0 bg-(--p-surface-high)">
      <button
        v-if="!props.hideClose"
        class="flex items-center justify-center opacity-60 shrink-0 cursor-pointer"
        @click="emit('close')"
      >
        <i class="ibi-close text-on-surface text-2xl leading-none" />
      </button>
      <span class="text-sm font-semibold text-on-surface uppercase tracking-wide leading-none">{{
        exchanges.length > 1 ? t('compareMarketRules') : t('Show_Market_Rules')
      }}</span>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex-1 flex items-center justify-center">
      <ProgressSpinner class="!h-8 !w-8" />
    </div>

    <!-- Content -->
    <div v-else class="flex-1 overflow-y-auto">
      <!-- Title Section -->
      <div class="px-6 py-4 flex flex-col gap-1">
        <h5 class="text-lg font-semibold text-on-surface leading-6">{{ t('exchangeMarketRules') }}</h5>
        <p class="text-base text-on-surface-secondary leading-5">
          {{ t('exchangeMarketRulesDesc') }}
          <a class="market-rules-link underline cursor-pointer" @click.prevent="openLink">{{
            t('exchangeMarketRulesDescLink')
          }}</a>
        </p>
      </div>

      <!-- Comparison Table -->
      <div class="overflow-x-auto mx-6">
        <div
          class="grid border-b border-surface-border"
          :style="{ gridTemplateColumns: `minmax(132px, max-content) repeat(${exchanges.length}, minmax(180px, 1fr))` }"
        >
          <!-- Exchange Header Row -->
          <div class="rules-label-cell flex items-end p-2 bg-surface font-semibold text-on-surface text-base">
            {{ t('exchangeLabel') }}
          </div>
          <div v-for="exch in exchanges" :key="`header-${exch}`" class="flex items-center gap-2.5 p-2 pr-4 bg-surface">
            <img
              :src="logoSrc(exch)"
              class="w-5 h-5 rounded-full shrink-0 outline outline-1 outline-black/10"
              :alt="exch"
            />
            <span class="font-semibold text-on-surface text-base whitespace-nowrap">{{ exch }}</span>
          </div>

          <!-- Section 1: Basic Info -->
          <template v-for="row in section1Rows" :key="row.label">
            <div class="rules-label-cell p-2 bg-surface border-t border-surface-border text-sm text-on-surface">
              {{ row.label }}
            </div>
            <div
              v-for="exch in exchanges"
              :key="`${row.label}-${exch}`"
              class="p-2 bg-surface border-t border-s border-surface-border text-sm text-on-surface"
            >
              <template v-if="row.type === 'link'">
                <a
                  v-if="getLinkHref(exch, row)"
                  :href="getLinkHref(exch, row)"
                  target="_blank"
                  rel="noopener"
                  class="text-primary underline cursor-pointer"
                  >{{ getCellValue(exch, row.field) || t('link') }}</a
                >
                <span v-else>{{ NODATA }}</span>
              </template>
              <template v-else>
                {{ getCellValue(exch, row.field) || NODATA }}
              </template>
            </div>
          </template>

          <!-- Divider -->
          <div class="col-span-full h-0.5 bg-surface-border" />

          <!-- Section 2: Trading Details (before Closing in) -->
          <template v-for="row in section2RowsBeforeClosing" :key="row.label">
            <div class="rules-label-cell p-2 bg-surface border-t border-surface-border text-sm text-on-surface">
              {{ row.label }}
            </div>
            <div
              v-for="exch in exchanges"
              :key="`${row.label}-${exch}`"
              class="p-2 bg-surface border-t border-s border-surface-border text-sm text-on-surface"
            >
              <template v-if="row.type === 'tick'">
                <div v-if="getTickDisplay(exch)">
                  <div v-for="(line, idx) in getTickDisplay(exch)" :key="idx">{{ line }}</div>
                </div>
                <span v-else>{{ getCellValue(exch, row.field) || NODATA }}</span>
              </template>
              <template v-else-if="row.type === 'status'">
                {{ getMarketStatus(exch) }}
              </template>
              <template v-else-if="row.type === 'date'">
                <div v-if="getDateValue(exch, row.field)">
                  <div>{{ getDateValue(exch, row.field)!.date }}</div>
                  <div>{{ getDateValue(exch, row.field)!.time }}</div>
                </div>
                <span v-else>{{ NODATA }}</span>
              </template>
              <template v-else>
                {{ getCellValue(exch, row.field) || NODATA }}
              </template>
            </div>
          </template>

          <!-- Closing in (countdown) -->
          <div class="rules-label-cell p-2 bg-surface border-t border-surface-border text-sm text-on-surface">
            {{ t('closingIn') }}
          </div>
          <div
            v-for="exch in exchanges"
            :key="`closing-${exch}`"
            class="p-2 bg-surface border-t border-s border-surface-border text-sm tabular-nums"
          >
            <span v-if="getClosingIn(exch)" :class="getClosingIn(exch)!.urgent ? 'text-negative' : 'text-on-surface'">{{
              getClosingIn(exch)!.text
            }}</span>
            <span v-else class="text-on-surface">{{ NODATA }}</span>
          </div>

          <!-- Section 2: Trading Details (after Closing in) -->
          <template v-for="row in section2RowsAfterClosing" :key="row.label">
            <div class="rules-label-cell p-2 bg-surface border-t border-surface-border text-sm text-on-surface">
              {{ row.label }}
            </div>
            <div
              v-for="exch in exchanges"
              :key="`${row.label}-${exch}`"
              class="p-2 bg-surface border-t border-s border-surface-border text-sm text-on-surface"
            >
              <template v-if="row.type === 'date'">
                <div v-if="getDateValue(exch, row.field)">
                  <div>{{ getDateValue(exch, row.field)!.date }}</div>
                  <div>{{ getDateValue(exch, row.field)!.time }}</div>
                </div>
                <span v-else>{{ NODATA }}</span>
              </template>
              <template v-else-if="row.type === 'link'">
                <a
                  v-if="getLinkHref(exch, row)"
                  :href="getLinkHref(exch, row)"
                  target="_blank"
                  rel="noopener"
                  class="text-on-surface underline cursor-pointer"
                  >{{ row.linkText || getCellValue(exch, row.field) || NODATA }}</a
                >
                <span v-else>{{ NODATA }}</span>
              </template>
              <template v-else>
                {{ getCellValue(exch, row.field) || NODATA }}
              </template>
            </div>
          </template>

          <!-- Divider -->
          <div class="col-span-full h-0.5 bg-surface-border" />

          <!-- Section 3: Settlement -->
          <template v-for="row in section3Rows" :key="row.label">
            <div class="rules-label-cell p-2 bg-surface border-t border-surface-border text-sm text-on-surface">
              {{ row.label }}
            </div>
            <div
              v-for="exch in exchanges"
              :key="`${row.label}-${exch}`"
              class="p-2 bg-surface border-t border-s border-surface-border text-sm text-on-surface"
            >
              <template v-if="row.type === 'link'">
                <a
                  v-if="getLinkHref(exch, row)"
                  :href="getLinkHref(exch, row)"
                  target="_blank"
                  rel="noopener"
                  class="text-on-surface underline cursor-pointer"
                  >{{ row.linkText || NODATA }}</a
                >
                <span v-else>{{ NODATA }}</span>
              </template>
              <template v-else>
                {{ getCellValue(exch, row.field) || NODATA }}
              </template>
            </div>
          </template>

          <!-- Bottom spacing -->
          <div class="col-span-full h-4 bg-surface" />
        </div>
      </div>
    </div>

    <Dialog
      v-model:visible="isLinkDialogOpen"
      :header="t('exchangeMarketRulesDescLink')"
      modal
      :draggable="false"
      :dismissableMask="true"
      :closeOnEscape="true"
      :contentClass="['lg:w-xl w-[90vw]']"
    >
      <div class="text-sm text-on-surface leading-relaxed" v-html="linkDialogContent" />
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useQueries } from '@tanstack/vue-query';
import { getContractRules } from '@/api/forecasttrader.api';
import { useUserState } from '@/stores/useUserState';
import { app } from '@ibgroup-tws-webapps/ratio.ui';
import type { ForecastTraderContractRulesResponse } from '@/types/forecasttrader.types';

export interface ExchangeConid {
  exchange: string;
  conid: number;
}

const props = withDefaults(
  defineProps<{
    exchangeConids: ExchangeConid[];
    hideClose?: boolean;
  }>(),
  { hideClose: false },
);

const emit = defineEmits<{
  close: [];
}>();

const { t } = useI18n();
const userState = useUserState();

const NODATA = '—';

const exchanges = computed(() => props.exchangeConids.map((ec) => ec.exchange));

const rulesQueries = useQueries({
  queries: computed(() =>
    props.exchangeConids.map((ec) => ({
      queryKey: ['forecasttrader', 'contract', 'rules', { conid: ec.conid }, userState.usePublicApi],
      queryFn: () => getContractRules({ conid: ec.conid }, userState.usePublicApi),
      staleTime: 60 * 60 * 1000,
      enabled: ec.conid > 0,
    })),
  ),
});

const isLoading = computed(() => rulesQueries.value.some((q) => q.isLoading));

const rulesMap = computed(() => {
  const map = new Map<string, ForecastTraderContractRulesResponse>();
  props.exchangeConids.forEach((ec, i) => {
    const data = rulesQueries.value[i]?.data;
    if (data) map.set(ec.exchange, data);
  });
  return map;
});

interface RowDef {
  label: string;
  field: keyof ForecastTraderContractRulesResponse;
  type?: 'text' | 'link' | 'date' | 'status' | 'tick';
  linkField?: keyof ForecastTraderContractRulesResponse;
  linkText?: string;
}

const section1Rows = computed<RowDef[]>(() => [
  { label: t('marketName'), field: 'market_name' },
  { label: t('productCode'), field: 'product_code' },
  { label: t('measuredPeriod'), field: 'measured_period' },
  { label: t('dataAndResolution'), field: 'source_agency', type: 'link', linkField: 'data_url_resolution_link' },
]);

const section2RowsBeforeClosing = computed<RowDef[]>(() => [
  { label: t('minimumTick'), field: 'price_increment', type: 'tick' },
  { label: t('currentStatus'), field: 'last_trade_time', type: 'status' },
  { label: t('lastTradeTime'), field: 'last_trade_time', type: 'date' },
]);

const section2RowsAfterClosing = computed<RowDef[]>(() => [
  { label: t('releaseDate'), field: 'release_time', type: 'date' },
  { label: t('payoutDate'), field: 'payout_time', type: 'date' },
]);

const section3Rows = computed<RowDef[]>(() => [
  { label: t('payout'), field: 'payout' },
  { label: '', field: 'market_rules_link', type: 'link', linkText: t('viewFullRules') },
]);

const isLinkDialogOpen = ref(false);

const linkDialogContent = t('exchangeMarketRulesDescLinkContent');

function openLink() {
  if (app && app.option.supportsDisplayContentAction && typeof app.send === 'function') {
    app.send({
      action: 'display_content',
      data: {
        title: '',
        body: linkDialogContent,
      },
    });
  } else {
    isLinkDialogOpen.value = true;
  }
}

function getRules(exchange: string): ForecastTraderContractRulesResponse | undefined {
  return rulesMap.value.get(exchange);
}

function getCellValue(exchange: string, field: keyof ForecastTraderContractRulesResponse): string {
  const rules = getRules(exchange);
  if (!rules) return '';
  const val = rules[field];
  if (val == null) return '';
  return String(val);
}

function getLinkHref(exchange: string, row: RowDef): string {
  const rules = getRules(exchange);
  if (!rules) return '';
  const linkField = row.linkField || row.field;
  const val = rules[linkField];
  return val ? String(val) : '';
}

function getDateValue(
  exchange: string,
  field: keyof ForecastTraderContractRulesResponse,
): { date: string; time: string } | null {
  const rules = getRules(exchange);
  if (!rules) return null;
  const epoch = rules[field] as number;
  if (!epoch) return null;
  return formatRulesDate(epoch, rules.exchange_timezone);
}

function formatRulesDate(epoch: number, timezone: string): { date: string; time: string } {
  const d = new Date(epoch * 1000);
  const dateStr = new Intl.DateTimeFormat('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: timezone || undefined,
  }).format(d);
  const timeStr = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone || undefined,
    timeZoneName: 'short',
  }).format(d);
  return { date: dateStr, time: `at ${timeStr}` };
}

function logoSrc(exchange: string): string {
  return `/tws.proxy/public/icons/forecastx?exchange=${exchange.toLowerCase()}`;
}

const now = ref(Date.now());
let tickInterval: ReturnType<typeof setInterval>;

onMounted(() => {
  tickInterval = setInterval(() => {
    now.value = Date.now();
  }, 1000);
});

onUnmounted(() => {
  clearInterval(tickInterval);
});

function getTickDisplay(exchange: string): string[] | null {
  const rules = getRules(exchange);
  if (!rules?.price_increments?.length) return null;
  const tiers = rules.price_increments;
  if (tiers.length === 1) return [tiers[0].increment];
  return tiers.map((tier, i) => {
    const lower = parseFloat(tier.lower_edge);
    const upper = i < tiers.length - 1 ? parseFloat(tiers[i + 1].lower_edge) - 0.01 : 1.0;
    return `${tier.increment} ($${lower.toFixed(2)} - $${upper.toFixed(2)})`;
  });
}

function getMarketStatus(exchange: string): string {
  const rules = getRules(exchange);
  if (!rules || !rules.last_trade_time) return NODATA;
  return rules.last_trade_time * 1000 > now.value ? t('marketOpen') : t('marketClosed');
}

function getClosingIn(exchange: string): { text: string; urgent: boolean } | null {
  const rules = getRules(exchange);
  if (!rules) return null;
  const epoch = rules.last_trade_time as number;
  if (!epoch) return null;
  const diffMs = epoch * 1000 - now.value;
  if (diffMs <= 0) return { text: '00:00:00', urgent: true };
  const totalSec = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  let text: string;
  if (days > 0) {
    text = `${days}d ${h}h ${m}m`;
  } else {
    text = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }
  return { text, urgent: days === 0 && h < 1 };
}
</script>

<style scoped>
.rules-label-cell {
  position: sticky;
  inset-inline-start: 0;
  z-index: 1;
  padding-inline-start: 1rem;
}
</style>
